import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Chart, LineController, LineElement, PointElement, LinearScale, Title, CategoryScale } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import './GraphPlaceholder.css';
import { authFetch } from '../authFetch';

// Ensure zoom plugin is properly available
console.log('[GraphPlaceholder] Zoom plugin imported:', zoomPlugin);

// Register plugins early
Chart.register(LineController, LineElement, PointElement, LinearScale, Title, CategoryScale, zoomPlugin);
console.log('[GraphPlaceholder] Plugins registered. Zoom plugin available:', !!Chart.registry.plugins.get('zoom'));

// ---------------- Debug Instrumentation Utilities ----------------
// Toggle via: window.__GRAPH_DEBUG = true/false OR localStorage.setItem('graphDebug','0'|'1')
const debugEnabled = () => {
  try {
    if (typeof window !== 'undefined') {
      if (window.__GRAPH_DEBUG === true) return true;
      if (window.__GRAPH_DEBUG === false) return false;
      const ls = window.localStorage.getItem('graphDebug');
      if (ls === '0' || ls === 'false') return false;
      if (ls === '1' || ls === 'true') return true;
    }
  } catch (_) {}
  return false; // Default OFF for production
};
const dLog = (...a) => { if (debugEnabled()) { try { console.log('[GraphDebug]', ...a); } catch(_){} } };
const dWarn = (...a) => { if (debugEnabled()) { try { console.warn('[GraphDebug]', ...a); } catch(_){} } };
const dGroup = (label, collapsed=true) => { if (!debugEnabled()) return; try { (collapsed?console.groupCollapsed:console.group)(`[GraphDebug] ${label}`); } catch(_){} };
const dGroupEnd = () => { if (!debugEnabled()) return; try { console.groupEnd(); } catch(_){} };
// ------------------------------------------------------------------

// Shared color palette for datasets and legend
const PALETTE = ['#ff0000','#0077ff','#00aa55','#ff9900','#9900ff','#00c2ff','#ff66cc','#666666'];

// API endpoint to fetch time-series data for a flight & columns
const READ_ULOG_API_ENDPOINT =
  process.env.REACT_APP_READ_ULOG_API_ENDPOINT ||
  'https://hjv8khvwpd.execute-api.eu-west-2.amazonaws.com/readUlogDataForGraph-dev';

// Livestream endpoint (new) for active flights.
// Prefer setting REACT_APP_READ_LIVESTREAM_API_ENDPOINT in a .env(.local) file.
// Fallback below is the deployed API you supplied.
const READ_LIVESTREAM_API_ENDPOINT = process.env.REACT_APP_READ_LIVESTREAM_API_ENDPOINT ||
  'https://6t9nlerxrk.execute-api.eu-west-2.amazonaws.com/readFlightLivestreamData';

const FAST_GRAPH_API_ENDPOINT = process.env.REACT_APP_FAST_GRAPH_API_ENDPOINT ||
  'https://per7598wpi.execute-api.eu-west-2.amazonaws.com/readFlightColumnsData-dev';

// cursor line plugin
const cursorLinePlugin = {
  id: 'cursorLine',
  afterDraw(chart, args, opts) {
    if (opts.currentTime === undefined) return;
    const xScale = chart.scales.x;
    if (!xScale) return;
    const xPos = xScale.getPixelForValue(opts.currentTime);
    const { ctx, chartArea: { top, bottom } } = chart;
    ctx.save();
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xPos, top);
    ctx.lineTo(xPos, bottom);
    ctx.stroke();
    ctx.restore();
  }
};

Chart.register(cursorLinePlugin);

// Simple in-memory cache: key -> Promise resolving to {times,data}
const dataCache = new Map();

// Optimized single-column fetch for parallel polling (bypasses some overhead)
export async function fetchSingleColumnCached(flightUuid, column, opts = {}) {
  if (!flightUuid || !column) {
    throw new Error('Invalid single column params');
  }
  
  // Use the existing multi-column function but with single column for consistency
  return await fetchGraphDataCached(flightUuid, [column], opts);
}

export async function fetchGraphDataCached(flightUuid, columns, opts = {}) {
  const start = performance.now?.() || Date.now();
  if (!flightUuid || !Array.isArray(columns) || columns.length === 0) {
    throw new Error('Invalid params');
  }

  // console.log("[fetchGraphDataCached] This logic here decides whether to fetch live or historical data, and this is where data falls through the cracks when its a finished 'live' flight data.")
  // console.log('[fetchGraphDataCached] opts:', opts);
  // console.log('[fetchGraphDataCached] live flag:', opts.live);
  // console.log('[fetchGraphDataCached] flightUuid:', flightUuid);
  // console.log('[fetchGraphDataCached] columns:', columns);

  const liveFlag = opts.live ? 'live' : 'hist';
  const key = `${flightUuid}|${liveFlag}|${columns.slice().sort().join(',')}`;
  if (!opts.live && dataCache.has(key)) {
    dLog('cacheHit', { key });
    return dataCache.get(key);
  }
  const buildPromise = async () => {
    const qs = new URLSearchParams({ flightUuid });
    columns.forEach(c => qs.append('columns', c));
    // Request more data points - API caps at 10000 max
    qs.append('limit', '10000');
    dLog('fetch:start', { flightUuid, columns, opts, key, qs: qs.toString() });

    if (opts.live) {
      if (READ_LIVESTREAM_API_ENDPOINT.includes('example.execute-api')) {
        dWarn('live:endpointPlaceholder');
      }
      try {
        const liveUrl = `${READ_LIVESTREAM_API_ENDPOINT}?${qs}`;
        const reqId = `live-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
        dLog('live:request', { liveUrl, reqId, cols: columns.length, strict: opts.liveStrict });
        const liveResp = await authFetch(liveUrl, { headers: { 'x-request-id': reqId, 'x-source-route': 'live' } });
        const status = liveResp.status;
        if (liveResp.ok) {
          const liveJson = await liveResp.json();
          if (liveJson && Array.isArray(liveJson.times) && liveJson.data && typeof liveJson.data === 'object') {
            dLog('live:success', { reqId, samples: liveJson.times.length });
            return { ...liveJson, _reqId: reqId };
          }
          const err = new Error('Malformed livestream response');
          err._liveDebug = `[LiveFetch] malformed response reqId=${reqId}`;
          dWarn('live:malformed', { reqId });
          throw err;
        }
        let bodyText = '';
        try { bodyText = await liveResp.text(); } catch(_) {}
        const snippet = bodyText ? bodyText.slice(0,200) : '';
        if (status === 404) {
          // Do NOT fallback to historical: livestream + just-finished share same DB.
          dLog('live:404-empty', { reqId });
          return { times: [], data: {}, launch_offset_s: null, _emptyLive: true, _reqId: reqId };
        }
        const err = new Error(`Livestream HTTP ${status}`);
        err._liveDebug = `[LiveFetch] status=${status} reqId=${reqId} flight=${flightUuid} cols=${columns.join(',')} url=${liveUrl} bodySnippet=${JSON.stringify(snippet)}`;
        dWarn('live:errorStatus', { status, reqId, snippet });
        throw err;
      } catch (e) {
        dWarn('live:exception', e);
        if (e instanceof Error) throw e;
        const err = new Error('Livestream fetch failed (unknown)');
        err._liveDebug = '[LiveFetch] unknown error';
        throw err;
      }
    }

    // Historical path
    const fastUrl = `${FAST_GRAPH_API_ENDPOINT}?${qs}`;
    dLog('histFast:request', { fastUrl });
    let fastResp = null;
    try {
      // Remove custom headers to avoid triggering a CORS preflight for dev localhost
      fastResp = await authFetch(fastUrl); // simple GET
    } catch (e) {
      dWarn('histFast:networkError', { message: e?.message });
    }
    if (fastResp && fastResp.ok) {
      try {
        const j = await fastResp.json();
        dLog('histFast:success', { samples: j?.times?.length });
        return j;
      } catch(parseErr) {
        dWarn('histFast:parseError', parseErr);
      }
    } else if (fastResp) {
      dWarn('histFast:nonOk', { status: fastResp.status });
    } else {
      dWarn('histFast:skippingToSlow', { reason: 'network or CORS' });
    }
    const slowResp = await authFetch(READ_ULOG_API_ENDPOINT, {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ flight_uuid: flightUuid, columns }),
    });
    if (!slowResp.ok) {
      const status = slowResp.status;
      const shouldTryLive = opts.allowLiveFallback === true && (status === 404 || (status >= 500 && status < 600));
      if (shouldTryLive) {
        try {
          const liveUrl = `${READ_LIVESTREAM_API_ENDPOINT}?${qs}`;
          const reqId = `fallback-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
          dLog('histFallback:attemptLive', { status, reqId, liveUrl });
          const fbResp = await authFetch(liveUrl, { headers: { 'x-request-id': reqId, 'x-source-route': 'hist-fallback-live' } });
          if (fbResp.ok) {
            const j = await fbResp.json();
            if (j && Array.isArray(j.times) && j.data && typeof j.data === 'object') {
              dLog('histFallback:liveSuccess', { reqId, samples: j.times.length });
              return { ...j, _reqId: reqId, _fallbackFromHist: true };
            }
          }
        } catch (fbErr) {
          dWarn('histFallback:liveException', fbErr);
        }
      }
      dWarn('histSlow:nonOk', { status });
      throw new Error(`HTTP ${status}`);
    }
    const slowJson = await slowResp.json();
    dLog('histSlow:success', { samples: slowJson?.times?.length });
    return slowJson;
  };
  const p = buildPromise().finally(() => dLog('fetch:done', { key, ms: (performance.now?.()||Date.now()) - start, live: opts.live }));
  if (!opts.live) dataCache.set(key, p);
  return p;
}

function GraphPlaceholder({ id, flightUuid, columns = [], onRemove, currentTime = 0, onResetGlobalTime, onUserSeek, playing = false, onTogglePlay, onDomain, customVariables = [], live = false, liveStrict = false, allowLiveFallback = false, disablePolling = false, heartbeatDelta = null, preventDataRefetch = false }) {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const launchOffsetRef = useRef(null);
  const userAdjustedRef = useRef(false); // set true after user zoom/pan so we don't auto-rescale domain
  const hasInitiallyLoadedRef = useRef(false); // Track if this graph has loaded data before
  const [isPanMode, setIsPanMode] = useState(false); // false -> zoom mode, true -> pan mode
  const [legendVisible, setLegendVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [scrubMode, setScrubMode] = useState(false);

  // Stable keys for dependency arrays (avoid JSON.stringify in deps which triggers lint warnings)
  const columnsKey = useMemo(() => columns.join(','), [columns]);
  const customVarsKey = useMemo(() => customVariables.map(cv => `${cv.name}:${cv.expression}`).join('|'), [customVariables]);

  // Helper function to log airspeed data samples
  const logAirspeedData = (times, data, source = 'unknown') => {
    if (data && data.airspeed && Array.isArray(data.airspeed) && Array.isArray(times)) {
      const airspeedData = data.airspeed;
      const len = Math.min(times.length, airspeedData.length);
      
      if (len === 0) {
        console.log(`[${source}] Airspeed: No data points`);
        return;
      }
      
      // Convert relative times to actual timestamps (assuming base timestamp)
      const baseTime = Date.now() - (times[times.length - 1] || 0) * 1000; // Rough estimate
      
      console.log(`[${source}] Airspeed data: ${len} total points`);
      
      // First 3 values
      const first3 = Math.min(3, len);
      console.log(`[${source}] First ${first3} airspeed values:`);
      for (let i = 0; i < first3; i++) {
        const timestamp = new Date(baseTime + times[i] * 1000).toISOString();
        console.log(`  [${i}] Time: ${times[i]}s (${timestamp}) - Airspeed: ${airspeedData[i]}`);
      }
      
      // Last 5 values
      const last5 = Math.min(5, len);
      console.log(`[${source}] Last ${last5} airspeed values:`);
      for (let i = len - last5; i < len; i++) {
        const timestamp = new Date(baseTime + times[i] * 1000).toISOString();
        console.log(`  [${i}] Time: ${times[i]}s (${timestamp}) - Airspeed: ${airspeedData[i]}`);
      }
    }
  };

  // Helper to build / rebuild chart (memoized so effects can safely depend on it)
  const buildChart = useCallback((times, seriesData, launchOffset) => {
    dGroup('ChartBuild', true);
    dLog('chart:inputs', { samples: times?.length, seriesKeys: Object.keys(seriesData||{}), launchOffset });
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const existing = chartInstanceRef.current;

    // ---- helpers ----
    const median = (arr) => {
      if (!arr.length) return 0;
      const a = arr.slice().sort((x,y)=>x-y);
      const m = Math.floor(a.length / 2);
      return a.length % 2 ? a[m] : (a[m-1] + a[m]) / 2;
    };

    // Collapse repeated x's to a single point to avoid vertical spikes
    const dedupeByX = (pts) => {
      if (pts.length <= 1) return pts;
      const out = [];
      let lastX;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        if (p.x === lastX) {
          out[out.length - 1] = p; // last wins
        } else {
          out.push(p);
          lastX = p.x;
        }
      }
      return out;
    };

    // Hampel filter
    const hampelMask = (ys, WINDOW = 7, SIGMA = 3.5) => {
      const n = ys.length, mask = new Array(n).fill(false);
      const half = Math.floor(WINDOW / 2), K = 1.4826;
      for (let i = 0; i < n; i++) {
        const s = Math.max(0, i - half);
        const e = Math.min(n, i + half + 1);
        const win = ys.slice(s, e).filter(Number.isFinite);
        if (win.length < 3) continue;
        const med = median(win);
        const mad = K * median(win.map(v => Math.abs(v - med)));
        if (!mad) continue;
        const diff = Math.abs(ys[i] - med);
        if (diff > SIGMA * mad) mask[i] = true;
      }
      return mask;
    };

    const interpolateNullsInPlace = (pts, maxGap) => {
      const n = pts.length;
      let i = 0;
      while (i < n) {
        if (pts[i].y != null) { i++; continue; }
        const left = i - 1;
        let j = i;
        while (j < n && pts[j].y == null) j++;
        const right = j;
        if (left >= 0 && right < n) {
          const gap = pts[right].x - pts[left].x;
            if (gap > 0 && gap <= maxGap) {
              const y0 = pts[left].y, y1 = pts[right].y, x0 = pts[left].x, x1 = pts[right].x;
              for (let k = left + 1; k < right; k++) {
                const t = (pts[k].x - x0) / (x1 - x0);
                pts[k].y = y0 + t * (y1 - y0);
              }
            }
        }
        i = right + 1;
      }
      return pts;
    };

    const breakLongGaps = (pts, maxGap) => {
      if (pts.length <= 1) return pts;
      const out = [pts[0]];
      for (let i = 1; i < pts.length; i++) {
        const prev = out[out.length - 1];
        const cur = pts[i];
        const dt = cur.x - prev.x;
        if (Number.isFinite(dt) && dt > maxGap) {
          out.push({ x: cur.x, y: null });
        }
        out.push(cur);
      }
      return out;
    };
    // -----------------

    const baseTimes = Array.isArray(times) ? times.map(Number) : [];
    const datasets = [];
    const allX = [];
    const baseSeriesProcessed = {};

    const processBaseColumn = (col) => {
      if (baseSeriesProcessed[col]) return baseSeriesProcessed[col];
      const arr = Array.isArray(seriesData[col]) ? seriesData[col] : [];
      const len = Math.min(arr.length, baseTimes.length);
      const pts = [];
      for (let i = 0; i < len; i++) {
        const x = Number(baseTimes[i]);
        if (!Number.isFinite(x)) continue;
        const rawY = arr[i];
        let y = null;
        if (rawY !== null && rawY !== undefined && rawY !== '') {
          const num = Number(rawY);
          if (Number.isFinite(num)) y = num;
        }
        pts.push({ x, y });
      }
      pts.sort((a,b)=>a.x-b.x);
      const uniq = dedupeByX(pts);
      let medDt = 0, maxGap = Number.POSITIVE_INFINITY;
      if (uniq.length > 2) {
        const dts = [];
        for (let i=1;i<uniq.length;i++) { const dt = uniq[i].x - uniq[i-1].x; if (dt>0) dts.push(dt); }
        medDt = median(dts);
        const GAP_MULTIPLIER = 10;
        maxGap = medDt > 0 ? medDt * GAP_MULTIPLIER : Number.POSITIVE_INFINITY;
      }
      const ys = uniq.map(p=>p.y);
      const mask = hampelMask(ys,7,3.5);
      const cleaned = uniq.map((p,i)=>({x:p.x,y:mask[i]?null:p.y}));
      interpolateNullsInPlace(cleaned, maxGap);
      const bridged = breakLongGaps(cleaned, maxGap);
      baseSeriesProcessed[col] = { points: bridged };
      return baseSeriesProcessed[col];
    };

    columns.forEach((col, idx) => {
      const { points } = processBaseColumn(col);
      for (const p of points) if (p.y != null) allX.push(p.x);
      datasets.push({
        label: col,
        data: points,
        spanGaps: false,
        fill: false,
        borderColor: PALETTE[idx % PALETTE.length],
        cubicInterpolationMode: 'monotone',
        tension: 0,
        pointRadius: 0,
        borderWidth: 1.2,
      });
    });

    const customRequested = customVariables.filter(cv => columns.includes(cv.name));

    const evaluateExpression = (expr, timeIndexToPoint) => {
      const tokens = expr.match(/dt\(|sqrt\(|[A-Za-z_][A-Za-z0-9_.]*|\d*\.\d+|\d+|[()+\-*/^]/g) || [];
      let pos = 0;
      const peek = () => tokens[pos];
      const consume = () => tokens[pos++];
      const parsePrimary = () => {
        let t = peek();
        if (t === '(') { consume(); const e = parseExpr(); if (peek() === ')') consume(); return e; }
        if (/^\d/.test(t)) { consume(); return { type:'num', value: Number(t) }; }
        if (t === 'sqrt(') { consume(); const inner = parseExpr(); if (peek() === ')') consume(); return { type:'func', name:'sqrt', args:[inner] }; }
        if (t === 'dt(') { consume(); const inner = parseExpr(); if (peek() === ')') consume(); return { type:'func', name:'dt', args:[inner] }; }
        if (/^[A-Za-z_]/.test(t)) { consume(); return { type:'var', name: t }; }
        consume(); return { type:'num', value: 0 }; };
      const parsePow = () => { let node = parsePrimary(); while (peek() === '^') { consume(); const rhs = parsePrimary(); node = { type:'op', op:'^', left: node, right: rhs }; } return node; };
      const parseTerm = () => { let node = parsePow(); while (peek() === '*' || peek() === '/') { const op = consume(); const rhs = parsePow(); node = { type:'op', op, left: node, right: rhs }; } return node; };
      const parseExpr = () => { let node = parseTerm(); while (peek() === '+' || peek() === '-') { const op = consume(); const rhs = parseTerm(); node = { type:'op', op, left: node, right: rhs }; } return node; };
      const ast = parseExpr();
      const evalNode = (node, i) => {
        switch(node.type) {
          case 'num': return node.value;
          case 'var': { const pt = timeIndexToPoint(node.name, i); return pt && pt.y != null ? pt.y : null; }
          case 'op': { const a = evalNode(node.left, i); const b = evalNode(node.right, i); if (a == null || b == null) return null; switch(node.op){ case '+': return a + b; case '-': return a - b; case '*': return a * b; case '/': return b !== 0 ? a / b : null; case '^': return Math.pow(a,b); default: return null; } }
          case 'func': { if (node.name === 'sqrt') { const v = evalNode(node.args[0], i); return v == null ? null : Math.sqrt(Math.max(0,v)); } if (node.name === 'dt') { if (i === 0) return null; const t0 = baseTimes[i-1]; const t1 = baseTimes[i]; if (!(t1 > t0)) return null; const v0 = evalNode(node.args[0], i-1); const v1 = evalNode(node.args[0], i); if (v0 == null || v1 == null) return null; return (v1 - v0) / (t1 - t0); } return null; }
          default: return null;
        }
      };
      return baseTimes.map((t, i) => ({ x: t, y: evalNode(ast, i) }));
    };

    const timeIndexToPoint = (varName, i) => {
      if (baseSeriesProcessed[varName]) {
        const pts = baseSeriesProcessed[varName].points; return pts[i] || null;
      }
      return null;
    };

    customRequested.forEach((cv, idx) => {
      cv.dependencies.forEach(dep => processBaseColumn(dep));
      const pts = evaluateExpression(cv.expression, timeIndexToPoint);
      for (const p of pts) if (p.y != null) allX.push(p.x);
      datasets.push({
        label: cv.name,
        data: pts,
        spanGaps: false,
        fill: false,
        borderColor: PALETTE[(columns.length + idx) % PALETTE.length],
        cubicInterpolationMode: 'monotone',
        tension: 0,
        pointRadius: 0,
        borderWidth: 1.2,
        borderDash: [4,2]
      });
    });

    if (allX.length === 0) {
      setError('No valid data points to plot');
      dWarn('chart:noDataPoints');
      dGroupEnd();
      return;
    }

    const medianX = median(allX);
    const reasonableUpper = medianX + 3600;
    const clippedAllX = allX.filter(x => x <= reasonableUpper);
    const xs = clippedAllX.length ? clippedAllX : allX;
    const xMinData = Math.min(...xs);
    const xMaxData = Math.max(...xs);
    const useLaunch = (launchOffset != null) && Number.isFinite(launchOffset) && (launchOffset < xMaxData);
    const xMin = useLaunch ? launchOffset : xMinData;
    const xMax = xMaxData;
    if (typeof onDomain === 'function' && Number.isFinite(xMin) && Number.isFinite(xMax) && xMax > xMin) {
      try { onDomain({ xMin, xMax, launchOffset }); } catch(e) { /* ignore */ }
    }
    const launchPlugin = { id: 'launchLine', afterDraw(chart, args, opts) { if (opts.launchOffset == null) return; const xScale = chart.scales.x; if (!xScale) return; const xPixel = xScale.getPixelForValue(opts.launchOffset); const { top, bottom } = chart.chartArea; const ctx2 = chart.ctx; ctx2.save(); ctx2.strokeStyle = '#000'; ctx2.setLineDash([]); ctx2.lineWidth = 2; ctx2.beginPath(); ctx2.moveTo(xPixel, top); ctx2.lineTo(xPixel, bottom); ctx2.stroke(); ctx2.restore(); } };
    Chart.register(launchPlugin);
    if (!existing) {
      const chart = new Chart(ctx, {
        type: 'line',
        data: { datasets },
        options: {
          parsing: false,
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 300 },
          plugins: {
            legend: { display: true, position: 'top', align: 'start', labels: { boxWidth: 12 } },
            zoom: {
              pan: { enabled: false, mode: 'x' },
              zoom: {
                wheel: { enabled: true, speed: 0.1 },
                pinch: { enabled: true },
                drag: { 
                  enabled: true, 
                  borderColor: '#007bff', 
                  borderWidth: 2,
                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                  threshold: 5
                },
                mode: 'x'
              },
              onZoom: (ctx) => { 
                userAdjustedRef.current = true; 
                dLog('zoom:event', { xMin: ctx.chart.scales.x.min, xMax: ctx.chart.scales.x.max });
              },
              onPan: (ctx) => { 
                userAdjustedRef.current = true; 
                dLog('pan:event', { xMin: ctx.chart.scales.x.min, xMax: ctx.chart.scales.x.max });
              }
            },
              cursorLine: { currentTime: 0 },
              launchLine: { launchOffset }
          },
          scales: {
            x: {
              type: 'linear',
              display: true,
              title: { display: true, text: 'Time (s)', font: { size: 12 } },
              min: xMin,
              max: xMax,
              grid: { display: true, color: 'rgba(0,0,0,0.15)' },
              border: { color: '#000' }
            },
            y: {
              display: true,
              title: { display: true, text: 'Value', font: { size: 12 } },
              grid: { display: true, color: 'rgba(0,0,0,0.15)' },
              border: { color: '#000' }
            }
          }
        }
      });
      chartInstanceRef.current = chart;
      launchOffsetRef.current = launchOffset;
      dLog('chart:ready:new', { datasets: datasets.length, xMin, xMax });
    } else {
      // Update in-place for smooth animation
      const chart = existing;
      // Update & add
      datasets.forEach(ds => {
        const found = chart.data.datasets.find(d => d.label === ds.label);
        if (found) {
          found.data = ds.data;
          found.borderColor = ds.borderColor;
          found.borderDash = ds.borderDash;
        } else {
          chart.data.datasets.push(ds);
        }
      });
      // Remove stale
      chart.data.datasets = chart.data.datasets.filter(d => datasets.some(nd => nd.label === d.label));
      // Auto-rescale only if user has not manually zoomed/panned
      if (!userAdjustedRef.current) {
        chart.options.scales.x.min = xMin;
        chart.options.scales.x.max = xMax;
      }
      launchOffsetRef.current = launchOffset;
      chart.update('normal');
      dLog('chart:ready:update', { datasets: datasets.length, xMin, xMax, userAdjusted: userAdjustedRef.current });
    }
    dGroupEnd();
  }, [columns, customVariables, onDomain]);

  // Fetch data whenever flight or column selection changes
  useEffect(() => {
    if (!flightUuid || columns.length === 0) return;
    
    // For finished livestreams: allow initial load but prevent subsequent refetches
    // For live flights: also prevent data refetches to avoid unwanted updates during variable selection
    // This allows new graphs to load but prevents existing graphs from updating during variable selection
    if (preventDataRefetch && hasInitiallyLoadedRef.current) {
      // console.log(`[GraphPlaceholder] Data refetch PREVENTED for ${live ? 'live flight' : 'finished livestream'} (already loaded):`, flightUuid);
      return;
    }
    
    // Expand requested columns to include base dependencies for custom variables.
    const fetchColsSet = new Set();
    columns.forEach(c => {
      const cv = customVariables.find(v => v.name === c);
      if (cv) {
        cv.dependencies.forEach(d => fetchColsSet.add(d));
      } else {
        fetchColsSet.add(c);
      }
    });
    const fetchCols = Array.from(fetchColsSet);

    let aborted = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let json;
        try {
          dLog('graph:source', { flightUuid, live, liveStrict, allowLiveFallback });
          json = await fetchGraphDataCached(flightUuid, fetchCols, { live, liveStrict, allowLiveFallback });
        } catch (err) {
          dWarn('initialFetch:cachedFetchError', err);
          throw err;
        }

        if (aborted) return;

        const { times = [], data = {}, launch_offset_s = null, _emptyLive } = json;
        
        // Log airspeed data if present
        logAirspeedData(times, data, 'InitialFetch');
        
        if (live && _emptyLive) {
          // Waiting for first samples; don't build an empty chart that would overwrite the message
          setError('Waiting for live data…');
          return;
        }
        buildChart(times, data, launch_offset_s);
        
        // Mark that this graph has completed its initial load
        hasInitiallyLoadedRef.current = true;
      } catch (err) {
        if (aborted) return;
        dWarn('initialFetch:error', { message: err?.message });
        // Distinguish live no-data vs actual error
        if (live) {
          setError(err.message || 'Live data error');
        } else {
          setError('Failed to load data');
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightUuid, columnsKey, customVarsKey, live, liveStrict, allowLiveFallback, buildChart, preventDataRefetch]);

  // Poll for live updates with smart intervals based on heartbeat age
  useEffect(() => {
    // Use 0.3s polling for all active flights (heartbeat < 30s) - optimized for responsiveness
    // Flights with heartbeat > 30s should have disablePolling=true, so won't reach here
    const pollingInterval = 300; // 0.3s for optimal graph updates
    
    // console.log('[PollingEffect] Setup check:', { 
    //   live, 
    //   flightUuid, 
    //   columnsCount: columns.length, 
    //   disablePolling,
    //   heartbeatDelta,
    //   pollingInterval,
    //   shouldPoll: live && flightUuid && columns.length > 0 && !disablePolling
    // });
    
    if (!live || !flightUuid || columns.length === 0 || disablePolling) {
      if (disablePolling) {
        // console.log('[PollingEffect] DISABLED for finished livestream:', flightUuid);
      }
      return;
    }
    
    // console.log('[PollingEffect] ENABLED for live flight:', flightUuid, 'interval:', pollingInterval + 'ms');
    let cancelled = false;
    
    // Track partial data for parallel updates
    const partialDataRef = { times: null, data: {}, launch_offset_s: null };
    
    const interval = setInterval(async () => {
      if (cancelled) return;
      // console.log('[PollingEffect] Polling tick for:', flightUuid);
      
      try {
        // Prepare columns to fetch
        const fetchColsSet = new Set();
        columns.forEach(c => {
          const cv = customVariables.find(v => v.name === c);
          if (cv) { cv.dependencies.forEach(d => fetchColsSet.add(d)); } else { fetchColsSet.add(c); }
        });
        const fetchCols = Array.from(fetchColsSet);
        
        // Fetch all columns in parallel (each as individual request)
        const columnPromises = fetchCols.map(async (col) => {
          try {
            const singleColData = await fetchSingleColumnCached(flightUuid, col, { live: true, liveStrict, allowLiveFallback });
            return { column: col, success: true, data: singleColData };
          } catch (err) {
            console.warn(`[PollingEffect] Column ${col} failed:`, err);
            return { column: col, success: false, error: err };
          }
        });
        
        // Wait for all column fetches (or timeout after reasonable period)
        const results = await Promise.allSettled(columnPromises);
        if (cancelled) return;
        
        // Process successful results and build combined data
        let newTimes = null;
        const newData = {};
        let newLaunchOffset = null;
        let hasAnyData = false;
        
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled' && result.value.success) {
            const { column, data: colData } = result.value;
            const { times = [], data = {}, launch_offset_s = null } = colData;
            
            // Use the first successful column's times as reference
            if (!newTimes && times.length > 0) {
              newTimes = times;
              newLaunchOffset = launch_offset_s;
            }
            
            // Add this column's data
            if (data[column]) {
              newData[column] = data[column];
              hasAnyData = true;
              
              // Log individual airspeed column data
              if (column === 'airspeed') {
                console.log(`[PollingColumn] Airspeed column received: ${data[column].length} values`);
                logAirspeedData(times, { airspeed: data[column] }, `Column-${column}`);
              }
            }
          }
        });
        
        if (!hasAnyData || !newTimes) {
          // console.log('[PollingEffect] No valid data from any column');
          return;
        }
        
        // Log airspeed data from polling if present
        logAirspeedData(newTimes, newData, 'PollingUpdate');
        
        // Update partial data reference
        partialDataRef.times = newTimes;
        partialDataRef.data = { ...partialDataRef.data, ...newData };
        if (newLaunchOffset !== null) partialDataRef.launch_offset_s = newLaunchOffset;
        
        const chart = chartInstanceRef.current;
        
        if (!chart) {
          // No chart exists yet, build it with available data
          // console.log('[PollingEffect] No chart exists, building new one with partial data');
          buildChart(partialDataRef.times, partialDataRef.data, partialDataRef.launch_offset_s);
          return;
        }
        
        // Check if we have new data points
        const currentLen = chart.data.datasets[0]?.data?.length || 0;
        const newLen = newTimes.length;
        
        if (newLen === currentLen) {
          dLog('poll:noChange', { len: newLen });
          // console.log('[PollingEffect] No new data:', { flightUuid, len: newLen });
          return;
        }
        
        // console.log('[PollingEffect] Updating chart data:', { flightUuid, prevLen: currentLen, newLen, availableCols: Object.keys(newData) });
        
        // Update chart data in place for better performance - only update columns that have new data
        const baseTimes = Array.isArray(newTimes) ? newTimes.map(Number) : [];
        
        // Process each column's data (only update if we have new data for it)
        columns.forEach((col, idx) => {
          if (!newData[col]) return; // Skip columns that didn't load this cycle
          
          const arr = Array.isArray(newData[col]) ? newData[col] : [];
          const len = Math.min(arr.length, baseTimes.length);
          const points = [];
          
          for (let i = 0; i < len; i++) {
            const x = Number(baseTimes[i]);
            if (!Number.isFinite(x)) continue;
            const rawY = arr[i];
            let y = null;
            if (rawY !== null && rawY !== undefined && rawY !== '') {
              const num = Number(rawY);
              if (Number.isFinite(num)) y = num;
            }
            points.push({ x, y });
          }
          
          // Sort by time
          points.sort((a, b) => a.x - b.x);
          
          // Update the dataset
          if (chart.data.datasets[idx]) {
            chart.data.datasets[idx].data = points;
            // console.log(`[PollingEffect] Updated column ${col} with ${points.length} points`);
          }
        });
        
        // Update x-axis range if user hasn't manually adjusted zoom
        if (!userAdjustedRef.current && baseTimes.length > 0) {
          const xMin = partialDataRef.launch_offset_s != null ? partialDataRef.launch_offset_s : Math.min(...baseTimes);
          const xMax = Math.max(...baseTimes);
          chart.options.scales.x.min = xMin;
          chart.options.scales.x.max = xMax;
        }
        
        // Smooth update without full rebuild
        chart.update('none'); // 'none' for no animation, 'active' for smooth animation
        dLog('poll:dataUpdate', { prevLen: currentLen, newLen, smooth: true, updatedCols: Object.keys(newData) });
        
      } catch (e) {
        dWarn('poll:error', e);
        console.error('[PollingEffect] Error:', { flightUuid, error: e });
      }
    }, pollingInterval);
    
    return () => { 
      cancelled = true; 
      clearInterval(interval); 
      // console.log('[PollingEffect] Cleanup for:', flightUuid);
    };
  }, [live, liveStrict, allowLiveFallback, flightUuid, columns, customVariables, columnsKey, customVarsKey, buildChart, disablePolling, heartbeatDelta]);
// (buildChart defined earlier via useCallback)


  // update cursor line on currentTime change
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    chart.options.plugins.cursorLine.currentTime = currentTime;
    chart.update('none');
    dLog('cursor:update', { currentTime });
  }, [currentTime]);

  // Attach click handler for scrub mode and debug drag events
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const handleClick = (evt) => {
      if (!scrubMode) return;
      const chart = chartInstanceRef.current;
      if (!chart) return;
      const x = evt.offsetX; // relative to canvas
      const xScale = chart.scales.x;
      if (!xScale) return;
      const time = xScale.getValueForPixel(x);
      if (typeof onUserSeek === 'function') {
        onUserSeek(time);
      }
      dLog('scrub:seek', { time });
    };
    
    // Debug event handlers
    const handleMouseDown = (evt) => {
      dLog('canvas:mousedown', { x: evt.offsetX, y: evt.offsetY, scrubMode, isPanMode });
    };
    
    const handleMouseMove = (evt) => {
      if (evt.buttons > 0) { // Only log when dragging
        dLog('canvas:mousemove:drag', { x: evt.offsetX, y: evt.offsetY, buttons: evt.buttons });
      }
    };
    
    const handleMouseUp = (evt) => {
      dLog('canvas:mouseup', { x: evt.offsetX, y: evt.offsetY });
    };
    
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
    };
  }, [scrubMode, isPanMode, onUserSeek]);

  const applyInteractionMode = (panEnabled) => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    chart.options.plugins.zoom.pan.enabled = panEnabled;
    chart.options.plugins.zoom.zoom.drag.enabled = !panEnabled;
    chart.update('none');
    dLog('interaction:modeChange', { panEnabled: panEnabled });
  };

  const handleToggleMode = () => {
    setIsPanMode(prev => {
      const newVal = !prev;
      applyInteractionMode(newVal);
      dLog('mode:toggle', { newMode: newVal ? 'pan' : 'zoom' });
      return newVal;
    });
  };

  const handleResetZoom = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.resetZoom();
      dLog('zoom:reset');
    }
    if (typeof onResetGlobalTime === 'function' && launchOffsetRef.current != null) {
      onResetGlobalTime(launchOffsetRef.current);
    }
  };

  // Test zoom programmatically
  const handleTestZoom = () => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    
    const xScale = chart.scales.x;
    if (!xScale) return;
    
    const currentMin = xScale.min;
    const currentMax = xScale.max;
    const range = currentMax - currentMin;
    const newMin = currentMin + range * 0.25;
    const newMax = currentMax - range * 0.25;
    
    chart.zoomScale('x', { min: newMin, max: newMax });
    dLog('zoom:test', { currentMin, currentMax, newMin, newMax });
  };

  const handleToggleScrub = () => setScrubMode(v=>!v);

  // Debug helper - expose to window for easy debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__enableGraphDebug = () => {
        window.__GRAPH_DEBUG = true;
        console.log('[GraphDebug] Debug enabled. Chart instance available at window.__debugChart');
        if (window.__debugChart) {
          console.log('[GraphDebug] Current zoom config:', window.__debugChart?.options?.plugins?.zoom);
        }
        console.log('[GraphDebug] Registered plugins:', Chart.registry.plugins.items);
      };
    }
  }, []); // Empty dependency array since this only needs to run once

  // Set debug chart reference when chart is created/updated
  useEffect(() => {
    if (typeof window !== 'undefined' && chartInstanceRef.current) {
      window.__debugChart = chartInstanceRef.current;
      // Immediate debug info when chart loads
      console.log('[GraphDebug] Chart created. Zoom plugin registered:', !!Chart.registry.plugins.get('zoom'));
      console.log('[GraphDebug] Initial zoom config:', chartInstanceRef.current?.options?.plugins?.zoom);
    }
  }, [loading]); // Depend on loading state instead of the ref

  // apply zoom settings when scrubMode toggles
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if(!chart) return;
    if(scrubMode){
      // disable all zoom/pan interactions
      chart.options.plugins.zoom.pan.enabled = false;
      chart.options.plugins.zoom.zoom.drag.enabled = false;
      chart.options.plugins.zoom.zoom.wheel.enabled = false;
      chart.options.plugins.zoom.zoom.pinch.enabled = false;
    } else {
      // restore based on pan mode state
      chart.options.plugins.zoom.pan.enabled = isPanMode;
      chart.options.plugins.zoom.zoom.drag.enabled = !isPanMode;
      chart.options.plugins.zoom.zoom.wheel.enabled = true;
      chart.options.plugins.zoom.zoom.pinch.enabled = true;
    }
    chart.update('none');
    dLog('scrubMode:apply', { scrubMode, isPanMode });
  }, [scrubMode, isPanMode]);

  const renderLegend = () => (
    <div className="graph-legend">
      {columns.map((col, idx) => (
        <span key={col} className="legend-item"><span className="legend-color" style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}></span>{col}</span>
      ))}
    </div>
  );

  return (
    <div className="graph-card" id={`graph-${id}`}>
      {loading && <div className="graph-loading">Loading…</div>}
      {!loading && !error && legendVisible && renderLegend()}
      {/* Mode indicator */}
      {!loading && !error && (
        <div className="graph-mode-indicator" style={{ 
          position: 'absolute', 
          top: '8px', 
          right: '8px', 
          fontSize: '0.7rem', 
          padding: '2px 6px', 
          backgroundColor: scrubMode ? '#ff9900' : (isPanMode ? '#28a745' : '#007bff'), 
          color: 'white', 
          borderRadius: '3px',
          zIndex: 10
        }}>
          {scrubMode ? 'SCRUB' : (isPanMode ? 'PAN' : 'ZOOM')}
        </div>
      )}
      {/* Help text for zoom mode */}
      {!loading && !error && !scrubMode && !isPanMode && (
        <div style={{ 
          position: 'absolute', 
          top: '28px', 
          right: '8px', 
          fontSize: '0.6rem', 
          padding: '2px 4px', 
          backgroundColor: 'rgba(0, 0, 0, 0.7)', 
          color: 'white', 
          borderRadius: '2px',
          zIndex: 9,
          maxWidth: '120px'
        }}>
          Drag to zoom in
        </div>
      )}
      {error && (
        <div className="graph-error">
          <div style={{ fontWeight: 600 }}>{error}</div>
          {live && (
            <>
              <div style={{ fontSize: '0.65rem', marginTop: 4, opacity: 0.8 }}>
                Live fetch failed. This graph does not fall back to historical data.
              </div>
              {/* Attempt to show last live request debug info if chart instance stored it later (future extension). */}
            </>
          )}
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: loading || error ? 'none' : 'block' }}></canvas>
      <button className="play-pause-btn" onClick={onTogglePlay} title={playing? 'Pause':'Play'} disabled={loading || error}>{playing? '⏸️':'▶'}</button>
      <button className="scrub-mode-btn" onClick={handleToggleScrub} title={scrubMode? 'Exit scrub mode':'Scrub mode'} disabled={loading || error}>{scrubMode? '🖱️':'⏲️'}</button>
      <button className="remove-graph-btn" onClick={onRemove} title="Remove graph">×</button>
      <button className="reset-zoom-btn" onClick={handleResetZoom} title="Reset zoom" disabled={loading || error}>↺</button>
      <button className="toggle-legend-btn" onClick={()=>setLegendVisible(v=>!v)} title={legendVisible? 'Hide labels':'Show labels'} disabled={loading || error}>🏷️</button>
      <button className="toggle-pan-btn" onClick={handleToggleMode} title={isPanMode ? 'Switch to zoom mode (drag to select area, scroll wheel to zoom)' : 'Switch to pan mode (drag to move view)'} disabled={loading || error}>
        {isPanMode ? '🔍' : '↔'}
      </button>
      <button className="test-zoom-btn" onClick={handleTestZoom} title="Test zoom programmatically" disabled={loading || error} style={{position: 'absolute', bottom: '6px', left: '6px', width: '22px', height: '22px', background: 'transparent', border: '1px solid #000', fontSize: '10px', cursor: 'pointer', zIndex: 2}}>
        Z
      </button>
    </div>
  );
}

export default GraphPlaceholder; 
